import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import DashboardLayout from '../components/DashboardLayout';
import AnnouncementComposeModal from '../components/AnnouncementComposeModal';
import AnnouncementDetailModal from '../components/AnnouncementDetailModal';
import { Megaphone, Plus, Trash2, Loader2, Users, ChevronRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PRIORITY_META = {
  info: { label: 'Info', badge: 'bg-slate-100 text-slate-700' },
  warning: { label: 'Warning', badge: 'bg-amber-100 text-amber-800' },
  urgent: { label: 'Urgent', badge: 'bg-red-100 text-red-700' },
};

const ROLE_LABEL = { company_admin: 'Admins', dispatcher: 'Dispatchers', driver: 'Drivers' };

const Announcements = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [detailId, setDetailId] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/announcements`, { withCredentials: true });
      setItems(r.data);
    } catch { toast.error('Could not load announcements'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const remove = async (item) => {
    if (!window.confirm(`Delete "${item.title}"?`)) return;
    try {
      await axios.delete(`${API}/announcements/${item.id}`, { withCredentials: true });
      toast.success('Deleted');
      fetchAll();
    } catch { toast.error('Failed'); }
  };

  const totals = useMemo(() => ({
    total: items.length,
    urgent: items.filter((i) => i.priority === 'urgent').length,
    read_pct: items.length > 0
      ? Math.round((items.reduce((s, i) => s + (i.stats?.read || 0), 0) /
                    Math.max(1, items.reduce((s, i) => s + (i.stats?.total || 0), 0))) * 100)
      : 0,
  }), [items]);

  return (
    <DashboardLayout>
      <div className="p-6 md:p-8" data-testid="announcements-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-3">
          <div>
            <div className="flex items-center gap-2">
              <Megaphone className="w-6 h-6 text-[#002FA7]" />
              <h1 className="text-3xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>
                Announcements
              </h1>
            </div>
            <p className="text-sm text-slate-600 mt-1">Broadcast alerts to your team with read-receipt tracking.</p>
          </div>
          <button
            data-testid="announcement-new-btn"
            onClick={() => setModal(true)}
            className="inline-flex items-center gap-2 bg-[#002FA7] text-white text-sm font-semibold rounded-md px-4 py-2 hover:bg-[#00247D]"
          >
            <Plus className="w-4 h-4" /> New announcement
          </button>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
          <div className="bg-white border border-slate-200 rounded-md p-4">
            <p className="text-xs font-semibold uppercase tracking-widest text-slate-500">Total broadcasts</p>
            <p className="text-2xl font-bold text-slate-900 mt-1" data-testid="summary-total">{totals.total}</p>
          </div>
          <div className="bg-white border border-red-200 rounded-md p-4">
            <p className="text-xs font-semibold uppercase tracking-widest text-red-600">Urgent</p>
            <p className="text-2xl font-bold text-red-700 mt-1" data-testid="summary-urgent">{totals.urgent}</p>
          </div>
          <div className="bg-white border border-slate-200 rounded-md p-4">
            <p className="text-xs font-semibold uppercase tracking-widest text-slate-500">Avg read rate</p>
            <p className="text-2xl font-bold text-slate-900 mt-1" data-testid="summary-read-rate">{totals.read_pct}%</p>
          </div>
        </div>

        <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
          {loading ? (
            <div className="p-10 text-center text-slate-500"><Loader2 className="w-4 h-4 animate-spin inline mr-2" />Loading…</div>
          ) : items.length === 0 ? (
            <div className="p-10 text-center text-slate-500" data-testid="announcements-empty">
              No announcements yet. Broadcast your first PSA to keep the team aligned.
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {items.map((a) => {
                const pri = PRIORITY_META[a.priority] || PRIORITY_META.info;
                const readPct = a.stats?.total ? Math.round((a.stats.read / a.stats.total) * 100) : 0;
                return (
                  <li key={a.id} className="px-4 py-3 hover:bg-slate-50" data-testid={`announcement-row-${a.id}`}>
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`text-xs font-bold px-2 py-0.5 rounded ${pri.badge}`}>{pri.label}</span>
                          {a.source === 'rehearsal' && (
                            <span className="text-xs font-bold px-2 py-0.5 rounded bg-slate-200 text-slate-700" data-testid={`rehearsal-badge-${a.id}`}>
                              REHEARSAL
                            </span>
                          )}
                          {a.status === 'scheduled' && (
                            <span className="text-xs font-bold px-2 py-0.5 rounded bg-blue-100 text-blue-700" data-testid={`scheduled-badge-${a.id}`}>
                              SCHEDULED · {a.scheduled_for ? new Date(a.scheduled_for).toLocaleString() : ''}
                            </span>
                          )}
                          {a.recurrence && a.recurrence !== 'none' && (
                            <span className="text-xs font-bold px-2 py-0.5 rounded bg-purple-100 text-purple-700">
                              {a.recurrence.toUpperCase()}
                            </span>
                          )}
                          <h3 className="text-sm font-semibold text-slate-900 truncate">{a.title}</h3>
                          <span className="text-xs text-slate-500">·  {new Date(a.created_at).toLocaleString()}</span>
                        </div>
                        <p className="text-sm text-slate-600 mt-1 line-clamp-2">{a.body}</p>
                        <div className="mt-2 flex items-center gap-3 text-xs text-slate-500">
                          <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" /> {(a.audience_roles || []).map((r) => ROLE_LABEL[r] || r).join(', ') || 'Everyone'}</span>
                          <span>·</span>
                          <span data-testid={`stats-${a.id}`}>{a.stats?.read || 0} / {a.stats?.total || 0} read ({readPct}%)</span>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setDetailId(a.id)}
                          data-testid={`view-${a.id}`}
                          className="text-[#002FA7] hover:text-[#00247D] text-xs font-semibold flex items-center gap-1"
                        >
                          Details <ChevronRight className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => remove(a)}
                          data-testid={`delete-${a.id}`}
                          className="text-red-600 hover:text-red-800"
                          title="Delete"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {modal && <AnnouncementComposeModal onClose={() => setModal(false)} onSaved={fetchAll} />}
        {detailId && <AnnouncementDetailModal id={detailId} onClose={() => setDetailId(null)} />}
      </div>
    </DashboardLayout>
  );
};

export default Announcements;
